import { Landmark } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { DocumentSection } from '@/components/app-document-detail';
import { MarangatuSnapshotView } from '@/components/marangatu-snapshot-view';
import { useSetSnapshot } from '@/hooks/use-set-snapshot';

/**
 * Card read-only en el panel del tenant con su PERFIL fiscal de Marangatu:
 * estado, cuenta corriente, obligaciones, actividades económicas, perfil y las
 * declaraciones que le corresponden. El saldo a favor y las últimas
 * presentaciones NO van acá (viven en Presentaciones de IVA / Renta), por eso
 * `showSaldo={false}` y `showPresentaciones={false}`.
 *
 * Consume `GET /api/app/set-access/snapshot` (scoped al propio tenant). NO expone
 * errores de scrape — si no hay datos muestra un estado neutro.
 */
export function AppFiscalProfileCard(): React.ReactElement | null {
  const { data, isLoading } = useSetSnapshot();
  const snap = data?.snapshot ?? null;
  const required = data?.requiredDeclarations ?? [];
  const hasData = snap != null && snap.status !== 'error';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Landmark className="size-4" />
          Perfil fiscal (SET)
        </CardTitle>
        <CardDescription>
          {hasData && snap?.scrapedAt
            ? `Datos de tu cuenta en Marangatu — actualizado el ${new Date(snap.scrapedAt).toLocaleDateString('es-PY')}.`
            : 'Tus datos oficiales de Marangatu (los actualiza tu contador).'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : !hasData ? (
          <p className="text-sm text-muted-foreground italic">
            Todavía no tenemos tus datos de Marangatu. Tu contador los sincroniza desde el sistema.
          </p>
        ) : (
          <div className="space-y-5">
            <MarangatuSnapshotView snapshot={snap} showSaldo={false} />
            {required.length > 0 && (
              <DocumentSection title="Declaraciones que te corresponden">
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {required.map((d) => (
                    <li key={d.codigo} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                      <span className="font-medium">{d.nombre}</span>
                      <span className="flex items-center gap-2 text-xs text-muted-foreground">
                        {d.formCode && <span className="font-mono">{d.formCode}</span>}
                        {d.cadencia && (
                          <Badge variant="secondary" className="text-[10px]">
                            {d.cadencia}
                          </Badge>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </DocumentSection>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
